import React, {useMemo, useState} from "react";
import {Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis} from "recharts";
import ChartCard from "./ChartCard.jsx";
import useStatisticsData from "./useStatisticsData.js";
import {formatEuro, formatEuroCompact, formatNumber, GROUP_PALETTE, monochromeScale} from "./format.js";

const LIMIT = 10;

/** #6 Top-Artikel (Renner) - horizontale Balken, Umsatz/Menge umschaltbar.
 * Eine einzelne Rangfolge -> einfarbige Abstufung hell/dunkel statt
 * kategorialer Farben. */
export default function TopArticlesChart({filters}) {
    const [metric, setMetric] = useState("net_amount");
    const params = useMemo(() => ({...filters, metric, limit: LIMIT}), [filters, metric]);
    const {data, loading, error} = useStatisticsData("/statistics/top-articles", params);
    const rows = data?.rows || [];

    const colors = useMemo(() => monochromeScale(GROUP_PALETTE[0], rows.length || 1), [rows.length]);
    const formatValue = metric === "quantity" ? (v) => `${formatNumber(v)} Stk.` : formatEuro;
    const formatAxis = metric === "quantity" ? formatNumber : formatEuroCompact;
    const height = Math.max(260, rows.length * 32);

    return (
        <ChartCard
            title="Top-Artikel"
            help="Die zehn umsatz- bzw. mengenstärksten Artikel im gewählten Zeitraum und Filter."
            loading={loading}
            error={error}
            empty={!loading && rows.length === 0}
            height={height}
            actions={
                <div className="btn-group btn-group-sm" role="group" aria-label="Kennzahl">
                    <button type="button" aria-pressed={metric === "net_amount"} className={`btn ${metric === "net_amount" ? "btn-primary" : "btn-outline-secondary"}`} onClick={() => setMetric("net_amount")}>Umsatz</button>
                    <button type="button" aria-pressed={metric === "quantity"} className={`btn ${metric === "quantity" ? "btn-primary" : "btn-outline-secondary"}`} onClick={() => setMetric("quantity")}>Menge</button>
                </div>
            }
        >
            <ResponsiveContainer width="100%" height={height}>
                <BarChart data={rows} layout="vertical" margin={{top: 4, right: 24, left: 8, bottom: 4}}>
                    <XAxis type="number" tickFormatter={formatAxis} tick={{fontSize: 12}} axisLine={false} tickLine={false} />
                    <YAxis type="category" dataKey="article_number" width={90} tick={{fontSize: 12}} axisLine={false} tickLine={false} />
                    <Tooltip
                        formatter={(value) => [formatValue(value), metric === "quantity" ? "Menge" : "Nettoumsatz"]}
                        labelFormatter={(label, payload) => payload?.[0]?.payload?.name || label}
                    />
                    <Bar dataKey={metric} radius={[0, 6, 6, 0]} barSize={16}>
                        {rows.map((row, index) => (
                            <Cell key={row.article_number} fill={colors[index]} />
                        ))}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </ChartCard>
    );
}
